if (!customElements.get('qr-bingo-demo')) {
  class QRBingoDemo extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.card = this.querySelector('[data-demo-card]');
      this.cells = Array.from(this.querySelectorAll('[data-demo-cell]'));
      this.watch = this.querySelector('[data-demo-watch]');
      this.resetButton = this.querySelector('[data-demo-reset]');
      this.question = this.querySelector('[data-demo-question]');
      this.status = this.querySelector('[data-demo-status]');
      this.counter = this.querySelector('[data-demo-step]');
      this.bingo = this.querySelector('[data-demo-bingo]');
      if (!this.card || !this.watch || !this.resetButton || !this.question || !this.status || this.cells.length !== 16 || this.cells.some((cell, index) => Number(cell.dataset.demoCell) !== index)) return;
      this.controller = new AbortController();
      this.watchTimers = new Set();
      this.runToken = (this.runToken || 0) + 1;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.lines = [
        ...Array.from({ length: 4 }, (_, row) => Array.from({ length: 4 }, (_, column) => row * 4 + column)),
        ...Array.from({ length: 4 }, (_, column) => Array.from({ length: 4 }, (_, row) => row * 4 + column)),
        [0, 5, 10, 15], [3, 6, 9, 12],
      ];
      this.reset();
      for (const [index, cell] of this.cells.entries()) {
        cell.disabled = false;
        cell.addEventListener('click', () => this.toggleCell(index), { signal: this.controller.signal });
      }
      this.watch.hidden = false;
      this.resetButton.hidden = false;
      this.watch.addEventListener('click', () => this.watchActive ? this.stopWatch(true) : this.startWatch(), { signal: this.controller.signal });
      this.resetButton.addEventListener('click', () => this.reset(), { signal: this.controller.signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden && this.watchActive) this.stopWatch(true);
      }, { signal: this.controller.signal });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.cancelCelebration();
      }, { signal: this.controller.signal });
    }
    copy(key) {
      return String(this.dataset[key] || '').trim();
    }
    reset() {
      this.stopWatch();
      this.cancelCelebration();
      this.marked = new Set();
      this.complete = false;
      this.classList.remove('is-complete');
      for (const cell of this.cells) {
        cell.classList.remove('is-marked', 'is-winning');
        cell.setAttribute('aria-pressed', 'false');
      }
      this.question.textContent = this.copy('manualQuestion');
      this.status.textContent = this.copy('ready');
      if (this.counter) this.counter.textContent = '0 / 16';
    }
    toggleCell(index) {
      this.stopWatch();
      this.setMark(index, !this.marked.has(index));
      this.status.textContent = this.complete ? this.copy('finish') : [this.cells[index].textContent.trim(), this.copy(this.marked.has(index) ? 'marked' : 'unmarked')].filter(Boolean).join(' ');
    }
    setMark(index, marked) {
      if (!Number.isInteger(index) || index < 0 || index >= this.cells.length) return;
      if (marked) this.marked.add(index);
      else this.marked.delete(index);
      this.cells[index].classList.toggle('is-marked', marked);
      this.cells[index].setAttribute('aria-pressed', String(marked));
      const winningLines = this.lines.filter(line => line.every(cell => this.marked.has(cell)));
      const winningCells = new Set(winningLines.flat());
      for (const [cellIndex, cell] of this.cells.entries()) cell.classList.toggle('is-winning', winningCells.has(cellIndex));
      const complete = winningLines.length > 0;
      this.classList.toggle('is-complete', complete);
      if (complete && !this.complete) this.celebrate();
      else if (!complete && this.complete) this.cancelCelebration();
      this.complete = complete;
      if (this.counter) this.counter.textContent = `${this.marked.size} / 16`;
    }
    startWatch() {
      this.reset();
      this.watchActive = true;
      this.watch.textContent = this.copy('next');
      this.watch.setAttribute('aria-pressed', 'true');
      this.playQuestion(0, this.runToken);
    }
    playQuestion(index, token) {
      if (!this.controller || !this.watchActive || token !== this.runToken) return;
      this.question.textContent = this.copy(`question-${index + 1}`);
      this.status.textContent = this.copy('watchStatus');
      this.scheduleWatch(() => {
        this.setMark(index, true);
        this.status.textContent = [this.copy(`response-${index + 1}`), this.complete ? this.copy('finish') : ''].filter(Boolean).join(' ');
        if (index === 3) this.stopWatch();
        else this.scheduleWatch(() => this.playQuestion(index + 1, token), 1400, token);
      }, 1200, token);
    }
    scheduleWatch(callback, delay, token) {
      const timer = setTimeout(() => {
        this.watchTimers.delete(timer);
        if (this.controller && this.watchActive && token === this.runToken) callback();
      }, delay);
      this.watchTimers.add(timer);
    }
    stopWatch(announce = false) {
      for (const timer of this.watchTimers || []) clearTimeout(timer);
      this.watchTimers?.clear();
      this.runToken = (this.runToken || 0) + 1;
      this.watchActive = false;
      if (this.watch) {
        this.watch.textContent = this.copy('reveal');
        this.watch.setAttribute('aria-pressed', 'false');
      }
      if (this.question) this.question.textContent = this.copy('manualQuestion');
      if (announce && this.status) this.status.textContent = this.copy('paused');
    }
    celebrate() {
      this.cancelCelebration();
      if (!this.bingo) return;
      this.bingo.hidden = false;
      if (!this.motion.matches) this.classList.add('is-celebrating');
      this.celebrationTimer = setTimeout(() => this.cancelCelebration(), 2400);
    }
    cancelCelebration() {
      clearTimeout(this.celebrationTimer);
      this.celebrationTimer = undefined;
      this.classList.remove('is-celebrating');
      if (this.bingo) this.bingo.hidden = true;
    }
    disconnectedCallback() {
      this.stopWatch();
      this.cancelCelebration();
      this.controller?.abort();
      this.controller = undefined;
    }
  }
  customElements.define('qr-bingo-demo', QRBingoDemo);
}
